import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  ArrowLeft,
  Settings,
  DollarSign,
  Edit,
  Trash2,
  Plus,
  Building2,
  Palette,
  RefreshCw,
  Database,
  Sliders,
  Megaphone,
  Home,
  Image,
  ArrowUp,
  ArrowDown,
  Save
} from "lucide-react";

export default function PricingAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [activeTab, setActiveTab] = useState("vexos");
  const [editingRule, setEditingRule] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedingExterior, setSeedingExterior] = useState(false);
  const [seedingMarketing, setSeedingMarketing] = useState(false);
  const [seedingInventory, setSeedingInventory] = useState(false);
  const [seedingInteriorTiers, setSeedingInteriorTiers] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addingToCategory, setAddingToCategory] = useState(null);
  const [newTier, setNewTier] = useState({ label: "", description: "", default_rate: 0 });
  // Usage stats (rule_id → used_count across all saved quote line items)
  const [usageStats, setUsageStats] = useState({});
  const [showUnusedOnly, setShowUnusedOnly] = useState(false);
  // Rendering volume tiers (flat-tier pricing for Interior + Exterior images)
  const [renderingTiers, setRenderingTiers] = useState({ interior: [], exterior: [] });
  const [tiersDirty, setTiersDirty] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);
  const [tierError, setTierError] = useState("");
  // VEXOS payment terms config
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [termsDirty, setTermsDirty] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const [termError, setTermError] = useState("");

  useEffect(() => {
    fetchRules();
    fetchUsageStats();
    fetchRenderingTiers();
    fetchPaymentTerms();
  }, []);

  async function fetchPaymentTerms() {
    try {
      const res = await apiClient.get("/calculators/payment-terms");
      setPaymentTerms(res.data?.terms || []);
    } catch { /* non-blocking */ }
  }

  function updateTerm(idx, field, value) {
    setTermError("");
    setPaymentTerms(prev => {
      const list = [...prev];
      let parsed = value;
      if (field === "months") parsed = value === "" ? 0 : Number(value);
      if (field === "is_extended" || field === "is_default" || field === "active") parsed = !!value;
      list[idx] = { ...list[idx], [field]: parsed };
      // Enforce single default
      if (field === "is_default" && parsed) {
        list.forEach((t, i) => { if (i !== idx) t.is_default = false; });
      }
      setTermsDirty(true);
      return list;
    });
  }

  function addTerm() {
    setTermError("");
    setPaymentTerms(prev => {
      const maxM = prev.length > 0 ? Math.max(...prev.map(t => t.months || 0)) : 0;
      setTermsDirty(true);
      return [...prev, { months: maxM + 3, label: null, subtext: null, is_extended: false, is_default: false, display_order: prev.length, active: true }];
    });
  }

  function removeTerm(idx) {
    setTermError("");
    setPaymentTerms(prev => {
      setTermsDirty(true);
      return prev.filter((_, i) => i !== idx).map((t, i) => ({ ...t, display_order: i }));
    });
  }

  function moveTerm(idx, direction) {
    setTermError("");
    setPaymentTerms(prev => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const list = [...prev];
      [list[idx], list[target]] = [list[target], list[idx]];
      setTermsDirty(true);
      return list.map((t, i) => ({ ...t, display_order: i }));
    });
  }

  async function savePaymentTerms() {
    setTermError("");
    setSavingTerms(true);
    try {
      await apiClient.put("/calculators/payment-terms", { terms: paymentTerms });
      toast.success("Payment terms saved");
      setTermsDirty(false);
      await fetchPaymentTerms();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save payment terms";
      setTermError(msg);
      toast.error(msg);
    } finally {
      setSavingTerms(false);
    }
  }

  async function fetchRenderingTiers() {
    try {
      const res = await apiClient.get("/calculators/rendering-tiers");
      setRenderingTiers(res.data || { interior: [], exterior: [] });
    } catch {
      // Non-blocking
    }
  }

  function addTier(category) {
    setTierError("");
    setRenderingTiers(prev => {
      const list = [...(prev[category] || [])];
      const lastMax = list.length > 0 ? Math.max(...list.map(t => t.max_qty ?? 0)) : 0;
      list.push({
        category,
        min_qty: lastMax + 1,
        max_qty: lastMax + 10,
        rate_per_image: 0,
        display_order: list.length,
        active: true,
      });
      setTiersDirty(true);
      return { ...prev, [category]: list };
    });
  }

  function updateTier(category, idx, field, value) {
    setTierError("");
    setRenderingTiers(prev => {
      const list = [...(prev[category] || [])];
      const parsed =
        field === "rate_per_image" || field === "min_qty"
          ? (value === "" ? 0 : Number(value))
          : field === "max_qty"
          ? (value === "" || value == null ? null : Number(value))
          : value;
      list[idx] = { ...list[idx], [field]: parsed };
      setTiersDirty(true);
      return { ...prev, [category]: list };
    });
  }

  function removeTier(category, idx) {
    setTierError("");
    setRenderingTiers(prev => {
      const list = (prev[category] || []).filter((_, i) => i !== idx).map((t, i) => ({ ...t, display_order: i }));
      setTiersDirty(true);
      return { ...prev, [category]: list };
    });
  }

  function moveTier(category, idx, direction) {
    setTierError("");
    setRenderingTiers(prev => {
      const list = [...(prev[category] || [])];
      const target = idx + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[idx], list[target]] = [list[target], list[idx]];
      const normalized = list.map((t, i) => ({ ...t, display_order: i }));
      setTiersDirty(true);
      return { ...prev, [category]: normalized };
    });
  }

  async function saveRenderingTiers() {
    setTierError("");
    setSavingTiers(true);
    try {
      await apiClient.put("/calculators/rendering-tiers", {
        interior: renderingTiers.interior || [],
        exterior: renderingTiers.exterior || [],
      });
      toast.success("Rendering tiers saved");
      setTiersDirty(false);
      await fetchRenderingTiers();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save tiers";
      setTierError(msg);
      toast.error(msg);
    } finally {
      setSavingTiers(false);
    }
  }

  async function fetchRules() {
    try {
      const res = await apiClient.get("/calculators/pricing-rules?active_only=false");
      setRules(res.data);
    } catch (err) {
      toast.error("Failed to load pricing rules");
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsageStats() {
    try {
      const res = await apiClient.get("/calculators/pricing-rules/usage-stats");
      setUsageStats(res.data?.usage || {});
    } catch {
      // Non-blocking — usage badge just won't appear if this fails
    }
  }

  // Helper — counts how many saved quote line items reference a rule
  function getRuleUsage(ruleId) {
    return usageStats[ruleId] ?? 0;
  }

  async function handleSeedRules() {
    if (!confirm("This will seed initial pricing rules. Continue?")) return;
    setSeeding(true);
    try {
      const res = await apiClient.post("/calculators/seed-pricing-rules");
      toast.success(res.data.message);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to seed rules");
    } finally {
      setSeeding(false);
    }
  }

  async function handleSeedExteriorPricing() {
    if (!confirm("This will seed complexity tiers and site environment pricing. Continue?")) return;
    setSeedingExterior(true);
    try {
      const res = await apiClient.post("/calculators/seed-exterior-pricing");
      toast.success(res.data.message);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to seed exterior pricing");
    } finally {
      setSeedingExterior(false);
    }
  }

  async function handleSeedMarketingPricing() {
    if (!confirm("This will seed marketing pricing rules. Continue?")) return;
    setSeedingMarketing(true);
    try {
      const res = await apiClient.post("/calculators/seed-marketing-pricing");
      toast.success(res.data.message);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to seed marketing pricing");
    } finally {
      setSeedingMarketing(false);
    }
  }

  async function handleAddTier() {
    if (!addingToCategory || !newTier.label) return;
    try {
      await apiClient.post("/calculators/pricing-rules", {
        calculator_type: "vexos",
        category: addingToCategory,
        label: newTier.label,
        description: newTier.description,
        pricing_method: "fixed",
        default_rate: parseFloat(newTier.default_rate) || 0,
        unit_type: addingToCategory === "complexity" ? "building" : "site",
        tier_config: { tier_value: newTier.label.toLowerCase().replace(/\s+/g, '_') },
        active: true,
        display_order: addingToCategory === "complexity" ? complexityRules.length + 1 : siteEnvRules.length + 1
      });
      toast.success("Tier added successfully");
      setShowAddDialog(false);
      setNewTier({ label: "", description: "", default_rate: 0 });
      setAddingToCategory(null);
      fetchRules();
    } catch (err) {
      toast.error("Failed to add tier");
    }
  }

  async function handleDeleteTier(rule) {
    if (!confirm(`Are you sure you want to delete "${rule.label}"?`)) return;
    try {
      await apiClient.delete(`/calculators/pricing-rules/${rule.id}`);
      toast.success("Tier deleted");
      fetchRules();
    } catch (err) {
      toast.error("Failed to delete tier");
    }
  }

  async function handleUpdateRule() {
    if (!editingRule) return;
    try {
      await apiClient.put(`/calculators/pricing-rules/${editingRule.id}`, {
        label: editingRule.label,
        description: editingRule.description,
        default_rate: editingRule.default_rate,
        rate_max: editingRule.rate_max,
        active: editingRule.active,
        ...(editingRule.tier_config ? { tier_config: editingRule.tier_config } : {}),
      });
      toast.success("Pricing rule updated");
      setShowEditDialog(false);
      setEditingRule(null);
      fetchRules();
    } catch (err) {
      toast.error("Failed to update rule");
    }
  }

  async function handleToggleActive(rule) {
    try {
      await apiClient.put(`/calculators/pricing-rules/${rule.id}`, {
        active: !rule.active
      });
      toast.success(rule.active ? "Rule deactivated" : "Rule activated");
      fetchRules();
    } catch (err) {
      toast.error("Failed to update rule");
    }
  }

  // Base rule list — apply the "Unused only" filter at the top so all derived lists inherit it.
  const visibleRules = showUnusedOnly
    ? rules.filter(r => getRuleUsage(r.id) === 0)
    : rules;
  const unusedTotal = rules.filter(r => getRuleUsage(r.id) === 0).length;

  const vexosRules = visibleRules.filter(r => r.calculator_type === "vexos" && r.category !== "complexity" && r.category !== "site_environment" && r.category !== "master_orbit" && r.category !== "additional_orbit");
  const vizRules = visibleRules.filter(r => r.calculator_type === "visualization" && !r.category?.startsWith("customizer_"));
  const complexityRules = visibleRules.filter(r => r.category === "complexity");
  const siteEnvRules = visibleRules.filter(r => r.category === "site_environment");
  const orbitRules = visibleRules.filter(r => r.category === "master_orbit" || r.category === "additional_orbit");
  const customizerRules = visibleRules.filter(r => r.category?.startsWith("customizer_"));
  const marketingRules = visibleRules.filter(r => r.calculator_type === "marketing");
  const inventoryRules = visibleRules.filter(r => r.calculator_type === "inventory");
  const interiorTierRules = visibleRules.filter(r => r.calculator_type === "interior_modeling");
  const hasExteriorPricing = complexityRules.length > 0 || siteEnvRules.length > 0;
  const hasOrbitPricing = orbitRules.length > 0;
  const hasCustomizerPricing = customizerRules.length > 0;
  const hasMarketingPricing = marketingRules.length > 0;
  const hasInventoryPricing = inventoryRules.length > 0;
  const hasInteriorTierPricing = interiorTierRules.length > 0;

  function formatRate(rule) {
    if (rule.pricing_method === "range" && rule.rate_max) {
      return `$${rule.default_rate.toLocaleString()} - $${rule.rate_max.toLocaleString()}`;
    }
    if (rule.pricing_method === "per_sqft") {
      return `$${rule.default_rate}/sqft`;
    }
    return `$${rule.default_rate.toLocaleString()}`;
  }

  function RulesTable({ rules, type }) {
    const groupedRules = rules.reduce((acc, rule) => {
      const cat = rule.category || "other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(rule);
      return acc;
    }, {});

    const categoryLabels = {
      platform: "Platform",
      floorplate: "Floorplates",
      addon: "Add-ons",
      recurring: "Recurring License",
      modeling: "3D Modeling",
      interior_design_direction: "Interior Design Direction",
      rendering: "Renderings",
      vr: "VR Tours",
      vr_residential: "VR Tours — Residential",
      vr_amenity: "VR Tours — Amenity",
      vr_addon: "VR Tours — Add-ons",
      animation: "Animation",
      video_animation: "Video Animation",
      campaign_fees: "Campaign Fees",
      cpl_pre_lease: "CPL Tiers — Pre-Lease",
      cpl_pre_sale: "CPL Tiers — Pre-Sale",
      infra_digital: "Digital Presence",
      infra_branding: "Branding & Positioning",
      infra_collateral: "Sales & Marketing Collateral",
      infra_ai_content: "AI-Enhanced Marketing Content",
      infra_ai_retainer: "AI Social Media Retainer Packages"
    };

    return (
      <div className="space-y-6">
        {Object.entries(groupedRules).map(([category, catRules]) => (
          <div key={category}>
            <h3 className="font-semibold text-sm text-muted-foreground mb-3 uppercase tracking-wide">
              {categoryLabels[category] || category}
            </h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[300px]">Item</TableHead>
                  <TableHead>Pricing Method</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catRules.sort((a, b) => a.display_order - b.display_order).map(rule => (
                  <TableRow key={rule.id} className={!rule.active ? "opacity-50" : ""}>
                    <TableCell>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {rule.label}
                          {getRuleUsage(rule.id) === 0 && (
                            <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30" data-testid="unused-rule-badge">Unused</span>
                          )}
                        </p>
                        {rule.description && (
                          <p className="text-xs text-muted-foreground">{rule.description}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {rule.pricing_method.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatRate(rule)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {rule.unit_type || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={rule.active ? "default" : "secondary"}
                        className={rule.active ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : ""}
                      >
                        {rule.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingRule({ ...rule });
                            setShowEditDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleActive(rule)}
                        >
                          {rule.active ? (
                            <Trash2 className="w-4 h-4 text-red-500" />
                          ) : (
                            <RefreshCw className="w-4 h-4 text-emerald-500" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/sales/calculators")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-light flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Pricing Administration
            </h1>
            <p className="text-muted-foreground">
              Manage pricing rules for calculators
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {rules.length > 0 && (
            <button
              type="button"
              onClick={() => setShowUnusedOnly(v => !v)}
              data-testid="toggle-unused-rules-filter"
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                showUnusedOnly
                  ? "bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-400"
                  : "bg-transparent border-border text-muted-foreground hover:bg-muted"
              }`}
              title="Toggle to show only pricing rules that have never been used in a saved quote"
            >
              {showUnusedOnly ? `Showing ${unusedTotal} unused` : `${unusedTotal} unused rules · filter`}
            </button>
          )}
          {rules.length === 0 && (
            <Button onClick={handleSeedRules} disabled={seeding} className="gap-2">
              <Database className="w-4 h-4" />
              {seeding ? "Seeding..." : "Seed Initial Pricing"}
            </Button>
          )}
        </div>
      </div>

      {rules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg">No Pricing Rules</h3>
            <p className="text-muted-foreground mt-1 mb-4">
              Click "Seed Initial Pricing" to populate with TRP's standard rates
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="vexos" className="gap-2">
              <Building2 className="w-4 h-4" />
              VEXOS ({vexosRules.length})
            </TabsTrigger>
            <TabsTrigger value="exterior" className="gap-2">
              <Settings className="w-4 h-4" />
              Exterior Modeling ({complexityRules.length + siteEnvRules.length + orbitRules.length})
            </TabsTrigger>
            <TabsTrigger value="visualization" className="gap-2">
              <Palette className="w-4 h-4" />
              Visualization ({vizRules.length})
            </TabsTrigger>
            <TabsTrigger value="customizer" className="gap-2">
              <Sliders className="w-4 h-4" />
              VR Customizer ({customizerRules.length})
            </TabsTrigger>
            <TabsTrigger value="marketing" className="gap-2">
              <Megaphone className="w-4 h-4" />
              Marketing ({marketingRules.length})
            </TabsTrigger>
            <TabsTrigger value="inventory" className="gap-2">
              <Database className="w-4 h-4" />
              Inventory ({inventoryRules.length})
            </TabsTrigger>
            <TabsTrigger value="interior_tiers" className="gap-2" data-testid="tab-interior-tiers">
              <Home className="w-4 h-4" />
              Interior Tiers ({interiorTierRules.length})
            </TabsTrigger>
            <TabsTrigger value="rendering_tiers" className="gap-2" data-testid="tab-rendering-tiers">
              <Image className="w-4 h-4" />
              Rendering Tiers ({(renderingTiers.interior?.length || 0) + (renderingTiers.exterior?.length || 0)})
            </TabsTrigger>
            <TabsTrigger value="payment_terms" className="gap-2" data-testid="tab-payment-terms">
              <Database className="w-4 h-4" />
              Payment Terms ({paymentTerms.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vexos">
            <Card>
              <CardHeader>
                <CardTitle>VEXOS Platform Pricing</CardTitle>
                <CardDescription>
                  Pricing rules for VEXOS interactive sales gallery platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RulesTable rules={vexosRules} type="vexos" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="exterior">
            {!hasExteriorPricing ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Settings className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium text-lg">No Exterior Pricing Tiers</h3>
                  <p className="text-muted-foreground mt-1 mb-4">
                    Click below to add complexity and site environment pricing tiers
                  </p>
                  <Button onClick={handleSeedExteriorPricing} disabled={seedingExterior} className="gap-2">
                    <Plus className="w-4 h-4" />
                    {seedingExterior ? "Seeding..." : "Add Exterior Pricing Tiers"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Complexity Tiers */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Primary Structure - Complexity Tiers</CardTitle>
                        <CardDescription>
                          Pricing tiers based on building size and complexity
                        </CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2"
                        onClick={() => {
                          setAddingToCategory("complexity");
                          setShowAddDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4" />
                        Add Tier
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {complexityRules.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground mb-4">No complexity tiers configured yet.</p>
                        <Button 
                          onClick={async () => {
                            try {
                              await apiClient.post("/calculators/seed-complexity-tiers");
                              toast.success("Complexity tiers added successfully");
                              fetchRules();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to seed complexity tiers");
                            }
                          }}
                          className="gap-2"
                        >
                          <Database className="w-4 h-4" />
                          Seed Default Complexity Tiers
                        </Button>
                      </div>
                    ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[250px]">Tier</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[120px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {complexityRules.sort((a, b) => a.display_order - b.display_order).map(rule => (
                          <TableRow key={rule.id} className={!rule.active ? "opacity-50" : ""}>
                            <TableCell className="font-medium">{rule.label}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">{rule.description}</TableCell>
                            <TableCell className="font-mono">${rule.default_rate.toLocaleString()}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={rule.active ? "default" : "secondary"}
                                className={rule.active ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : ""}
                              >
                                {rule.active ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setEditingRule({ ...rule });
                                    setShowEditDialog(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleActive(rule)}
                                >
                                  {rule.active ? (
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4 text-emerald-500" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    )}
                  </CardContent>
                </Card>

                {/* Site Environment Tiers */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Site Environment Tiers</CardTitle>
                        <CardDescription>
                          Pricing tiers for surrounding environment modeling
                        </CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2"
                        onClick={() => {
                          setAddingToCategory("site_environment");
                          setShowAddDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4" />
                        Add Tier
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[250px]">Tier</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Rate</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-[120px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {siteEnvRules.sort((a, b) => a.display_order - b.display_order).map(rule => (
                          <TableRow key={rule.id} className={!rule.active ? "opacity-50" : ""}>
                            <TableCell className="font-medium">{rule.label}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">{rule.description}</TableCell>
                            <TableCell className="font-mono">${rule.default_rate.toLocaleString()}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={rule.active ? "default" : "secondary"}
                                className={rule.active ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : ""}
                              >
                                {rule.active ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setEditingRule({ ...rule });
                                    setShowEditDialog(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleActive(rule)}
                                >
                                  {rule.active ? (
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4 text-emerald-500" />
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Orbit Pricing */}
                <Card className="mt-6">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Orbit Pricing</CardTitle>
                        <CardDescription>
                          Pricing for Suborbits and Additional Specialty Orbits
                        </CardDescription>
                      </div>
                      {!hasOrbitPricing && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2"
                          onClick={async () => {
                            try {
                              await apiClient.post("/calculators/seed-orbit-pricing");
                              toast.success("Orbit pricing rules added");
                              fetchRules();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to seed orbit pricing");
                            }
                          }}
                        >
                          <Database className="w-4 h-4" />
                          Seed Orbit Pricing
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {orbitRules.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground mb-4">No orbit pricing configured yet.</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[250px]">Orbit Type</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Rate</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-[120px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orbitRules.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map(rule => (
                            <TableRow key={rule.id} className={!rule.active ? "opacity-50" : ""}>
                              <TableCell className="font-medium">{rule.label}</TableCell>
                              <TableCell className="text-muted-foreground text-sm">{rule.description}</TableCell>
                              <TableCell className="font-mono">${rule.default_rate.toLocaleString()}</TableCell>
                              <TableCell>
                                <Badge 
                                  variant={rule.active ? "default" : "secondary"}
                                  className={rule.active ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : ""}
                                >
                                  {rule.active ? "Active" : "Inactive"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setEditingRule({ ...rule });
                                      setShowEditDialog(true);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleToggleActive(rule)}
                                  >
                                    {rule.active ? (
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4 text-emerald-500" />
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="visualization">
            <Card>
              <CardHeader>
                <CardTitle>Visualization Services Pricing</CardTitle>
                <CardDescription>
                  Pricing rules for renderings, VR tours, and animation services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RulesTable rules={vizRules} type="visualization" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="customizer">
            {!hasCustomizerPricing ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Sliders className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium text-lg">No Customizer Pricing</h3>
                  <p className="text-muted-foreground mt-1 mb-4">
                    Click below to add VR Tour Real-Time Customizer pricing
                  </p>
                  <Button 
                    onClick={async () => {
                      try {
                        await apiClient.post("/calculators/seed-customizer-pricing");
                        toast.success("Customizer pricing rules added");
                        fetchRules();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to seed customizer pricing");
                      }
                    }}
                    className="gap-2"
                  >
                    <Database className="w-4 h-4" />
                    Seed Customizer Pricing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>VR Tour Real-Time Customizer Pricing</CardTitle>
                      <CardDescription>
                        Pricing for interactive finish, material, and upgrade selection experiences in VR tours
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[280px]">Pricing Element</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[120px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customizerRules.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map(rule => (
                        <TableRow key={rule.id} className={!rule.active ? "opacity-50" : ""}>
                          <TableCell className="font-medium">{rule.label}</TableCell>
                          <TableCell className="text-muted-foreground text-sm max-w-md">{rule.description}</TableCell>
                          <TableCell className="font-mono">
                            {rule.category === "customizer_options" 
                              ? rule.default_rate
                              : `$${rule.default_rate.toLocaleString()}`
                            }
                          </TableCell>
                          <TableCell className="text-muted-foreground capitalize">{rule.unit_type || "—"}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={rule.active ? "default" : "secondary"}
                              className={rule.active ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : ""}
                            >
                              {rule.active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setEditingRule({ ...rule });
                                  setShowEditDialog(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleToggleActive(rule)}
                              >
                                {rule.active ? (
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                ) : (
                                  <RefreshCw className="w-4 h-4 text-emerald-500" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  <div className="mt-6 p-4 rounded-lg bg-muted/50">
                    <h4 className="font-medium mb-2">Standard Option Pricing</h4>
                    <p className="text-sm text-muted-foreground">
                      Option pricing is tiered based on quantity (applies to most categories):
                    </p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                      <li><strong>Base Activation Fee</strong> - charged once per tour type with customizer</li>
                      <li><strong>1 Option</strong> - ${customizerRules.find(r => r.category === "customizer_option_tier1")?.default_rate?.toLocaleString() || "800"} per option</li>
                      <li><strong>2 Options</strong> - ${customizerRules.find(r => r.category === "customizer_option_tier2")?.default_rate?.toLocaleString() || "700"} per option</li>
                      <li><strong>3+ Options</strong> - ${customizerRules.find(r => r.category === "customizer_option_tier3plus")?.default_rate?.toLocaleString() || "500"} per option</li>
                    </ul>
                  </div>
                  
                  <div className="mt-4 p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                    <h4 className="font-medium mb-2 text-purple-700">Furniture Package (Premium)</h4>
                    <p className="text-sm text-muted-foreground">
                      Furniture Package has premium pricing due to complexity:
                    </p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1 list-disc list-inside">
                      <li><strong>1 Option</strong> - ${customizerRules.find(r => r.category === "customizer_furniture_tier1")?.default_rate?.toLocaleString() || "1,800"} per option</li>
                      <li><strong>2 Options</strong> - ${customizerRules.find(r => r.category === "customizer_furniture_tier2")?.default_rate?.toLocaleString() || "1,500"} per option</li>
                      <li><strong>3 Options</strong> - ${customizerRules.find(r => r.category === "customizer_furniture_tier3")?.default_rate?.toLocaleString() || "1,200"} per option</li>
                      <li><strong>4+ Options</strong> - ${customizerRules.find(r => r.category === "customizer_furniture_tier4plus")?.default_rate?.toLocaleString() || "1,000"} per option</li>
                    </ul>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mt-4">
                    <strong>Formula:</strong> Category Total = Option Count × Tier Price
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="marketing">
            {!hasMarketingPricing ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Megaphone className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium text-lg">No Marketing Pricing</h3>
                  <p className="text-muted-foreground mt-1 mb-4">
                    Click below to add marketing service pricing (campaign fees, CPL tiers, infrastructure & collateral)
                  </p>
                  <Button onClick={handleSeedMarketingPricing} disabled={seedingMarketing} className="gap-2">
                    <Database className="w-4 h-4" />
                    {seedingMarketing ? "Seeding..." : "Seed Marketing Pricing"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Marketing Services Pricing</CardTitle>
                  <CardDescription>
                    Campaign fees, CPL tiers, and infrastructure & collateral pricing
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RulesTable rules={marketingRules} type="marketing" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="inventory">
            {!hasInventoryPricing ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Database className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium text-lg">No Inventory Pricing Tiers</h3>
                  <p className="text-muted-foreground mt-1 mb-4">
                    Seed default volume-based tiers for the Residential Inventory field in the Combined Calculator
                  </p>
                  <Button onClick={async () => {
                    setSeedingInventory(true);
                    try {
                      const res = await apiClient.post("/calculators/seed-inventory-tiers");
                      toast.success(res.data.message);
                      fetchRules();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to seed inventory tiers");
                    } finally {
                      setSeedingInventory(false);
                    }
                  }} disabled={seedingInventory} className="gap-2">
                    <Database className="w-4 h-4" />
                    {seedingInventory ? "Seeding..." : "Seed Inventory Tiers"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Inventory Volume Pricing</CardTitle>
                  <CardDescription>
                    Flat rate per unit based on total inventory count. Edit rates, unit ranges, and labels below.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tier Label</TableHead>
                        <TableHead>Min Units</TableHead>
                        <TableHead>Max Units</TableHead>
                        <TableHead className="text-right">Rate / Unit</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inventoryRules.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map(rule => (
                        <TableRow key={rule.id}>
                          <TableCell className="font-medium">{rule.label}</TableCell>
                          <TableCell>{rule.tier_config?.min_units?.toLocaleString() ?? "—"}</TableCell>
                          <TableCell>{rule.tier_config?.max_units >= 999999999 ? "∞" : rule.tier_config?.max_units?.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">${rule.default_rate}</TableCell>
                          <TableCell>
                            <Badge variant={rule.active ? "default" : "secondary"}>
                              {rule.active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => { setEditingRule(rule); setShowEditDialog(true); }}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="interior_tiers">
            {!hasInteriorTierPricing ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Home className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-medium text-lg">No Interior Modeling Tiers</h3>
                  <p className="text-muted-foreground mt-1 mb-4 max-w-lg mx-auto">
                    Seed the default volume-based interior modeling tiers. One rate is selected from the combined
                    residential + amenity SF and applied uniformly to both.
                  </p>
                  <Button onClick={async () => {
                    setSeedingInteriorTiers(true);
                    try {
                      const res = await apiClient.post("/calculators/seed-interior-modeling-tiers");
                      toast.success(res.data.message);
                      fetchRules();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to seed interior modeling tiers");
                    } finally {
                      setSeedingInteriorTiers(false);
                    }
                  }} disabled={seedingInteriorTiers} className="gap-2" data-testid="seed-interior-tiers-btn">
                    <Home className="w-4 h-4" />
                    {seedingInteriorTiers ? "Seeding..." : "Seed Interior Modeling Tiers"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Interior Modeling Volume Tiers</CardTitle>
                  <CardDescription>
                    Single-tier (non-graduated) pricing. One rate is selected based on total combined
                    residential + amenity interior SF and applied to all of it. Edit rates, SF ranges, and labels below.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tier Label</TableHead>
                        <TableHead>Min SF</TableHead>
                        <TableHead>Max SF</TableHead>
                        <TableHead className="text-right">Rate / SF</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {interiorTierRules.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)).map(rule => (
                        <TableRow key={rule.id} data-testid={`interior-tier-row-${rule.id}`}>
                          <TableCell className="font-medium">{rule.label}</TableCell>
                          <TableCell>{rule.tier_config?.min_sqft?.toLocaleString() ?? "—"}</TableCell>
                          <TableCell>{rule.tier_config?.max_sqft >= 999999999 ? "∞" : rule.tier_config?.max_sqft?.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">${rule.default_rate?.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant={rule.active ? "default" : "secondary"}>
                              {rule.active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => { setEditingRule(rule); setShowEditDialog(true); }} data-testid={`edit-interior-tier-${rule.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="rendering_tiers">
            <div className="space-y-6">
              {tierError && (
                <div className="border border-red-500/40 bg-red-500/5 text-red-600 dark:text-red-400 text-sm rounded-md px-4 py-3" data-testid="tier-error">
                  {tierError}
                </div>
              )}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2"><Image className="w-5 h-5" />Rendering Volume Tiers</CardTitle>
                      <CardDescription>
                        Flat-tier pricing for Interior and Exterior images. A client with 35 interior images pays the 31–50 tier rate on ALL 35 images (not graduated). Leave the final tier's "Max Qty" empty for an unlimited open-ended bracket.
                      </CardDescription>
                    </div>
                    <Button
                      onClick={saveRenderingTiers}
                      disabled={!tiersDirty || savingTiers}
                      data-testid="save-rendering-tiers-btn"
                      className="gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {savingTiers ? "Saving..." : tiersDirty ? "Save Changes" : "Saved"}
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {["interior","exterior"].map(cat => (
                <Card key={cat}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="capitalize text-base">{cat} Images</CardTitle>
                      <Button size="sm" variant="outline" onClick={() => addTier(cat)} className="gap-1" data-testid={`add-${cat}-tier-btn`}>
                        <Plus className="w-3.5 h-3.5" /> Add Tier
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Min Qty</TableHead>
                          <TableHead>Max Qty</TableHead>
                          <TableHead>Rate / Image</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(renderingTiers[cat] || []).map((t, idx) => (
                          <TableRow key={t.id || `${cat}-${idx}`} data-testid={`${cat}-tier-row-${idx}`}>
                            <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                value={t.min_qty ?? ""}
                                onChange={(e) => updateTier(cat, idx, "min_qty", e.target.value)}
                                className="h-8 w-24"
                                data-testid={`${cat}-tier-${idx}-min`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                placeholder="Unlimited"
                                value={t.max_qty ?? ""}
                                onChange={(e) => updateTier(cat, idx, "max_qty", e.target.value)}
                                className="h-8 w-24"
                                data-testid={`${cat}-tier-${idx}-max`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-muted-foreground">$</span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="50"
                                  value={t.rate_per_image ?? 0}
                                  onChange={(e) => updateTier(cat, idx, "rate_per_image", e.target.value)}
                                  className="h-8 w-28"
                                  data-testid={`${cat}-tier-${idx}-rate`}
                                />
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="inline-flex gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveTier(cat, idx, -1)} disabled={idx === 0}>
                                  <ArrowUp className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveTier(cat, idx, 1)} disabled={idx === (renderingTiers[cat] || []).length - 1}>
                                  <ArrowDown className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeTier(cat, idx)} data-testid={`${cat}-tier-${idx}-remove`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {(renderingTiers[cat] || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                              No tiers defined. Click "Add Tier" to create one.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
              <p className="text-xs text-muted-foreground italic">
                Volume pricing is applied as a flat tier based on total image quantity. Tier ranges must not overlap. Only one tier (the last) may have an unlimited max.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="payment_terms">
            <div className="space-y-6">
              {termError && (
                <div className="border border-red-500/40 bg-red-500/5 text-red-600 dark:text-red-400 text-sm rounded-md px-4 py-3" data-testid="payment-term-error">
                  {termError}
                </div>
              )}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2"><Database className="w-5 h-5" />VEXOS Payment Terms</CardTitle>
                      <CardDescription>
                        Configure which payment term options appear in the VEXOS Payment Plan module. Mark one as "Default" (pre-selected on new quotes) and any longer plans as "Extended" — those render below the main chips with a distinguishing badge.
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={addTerm} className="gap-1" data-testid="add-payment-term-btn">
                        <Plus className="w-3.5 h-3.5" /> Add Term
                      </Button>
                      <Button
                        onClick={savePaymentTerms}
                        disabled={!termsDirty || savingTerms}
                        className="gap-2"
                        data-testid="save-payment-terms-btn"
                      >
                        <Save className="w-4 h-4" />
                        {savingTerms ? "Saving..." : termsDirty ? "Save Changes" : "Saved"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Months</TableHead>
                        <TableHead>Badge Label</TableHead>
                        <TableHead>Subtext</TableHead>
                        <TableHead className="text-center">Extended</TableHead>
                        <TableHead className="text-center">Default</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentTerms.map((t, idx) => (
                        <TableRow key={t.id || `term-${idx}`} data-testid={`payment-term-row-${idx}`}>
                          <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                          <TableCell>
                            <Input type="number" min="1" value={t.months ?? 0} onChange={(e) => updateTerm(idx, "months", e.target.value)} className="h-8 w-20" data-testid={`payment-term-${idx}-months`} />
                          </TableCell>
                          <TableCell>
                            <Input placeholder="Extended Plan" value={t.label ?? ""} onChange={(e) => updateTerm(idx, "label", e.target.value)} className="h-8" />
                          </TableCell>
                          <TableCell>
                            <Input placeholder="Lower monthly payments" value={t.subtext ?? ""} onChange={(e) => updateTerm(idx, "subtext", e.target.value)} className="h-8" />
                          </TableCell>
                          <TableCell className="text-center">
                            <input type="checkbox" checked={!!t.is_extended} onChange={(e) => updateTerm(idx, "is_extended", e.target.checked)} data-testid={`payment-term-${idx}-extended`} />
                          </TableCell>
                          <TableCell className="text-center">
                            <input type="checkbox" checked={!!t.is_default} onChange={(e) => updateTerm(idx, "is_default", e.target.checked)} data-testid={`payment-term-${idx}-default`} />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveTerm(idx, -1)} disabled={idx === 0}>
                                <ArrowUp className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => moveTerm(idx, 1)} disabled={idx === paymentTerms.length - 1}>
                                <ArrowDown className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeTerm(idx)} data-testid={`payment-term-${idx}-remove`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {paymentTerms.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                            No payment terms defined. Click "Add Term" to create one.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <p className="text-xs text-muted-foreground italic">
                Primary terms (non-extended) render as an equal-column grid. Extended terms render below a divider with a "Extended Plan" badge and optional subtext.
              </p>
            </div>
          </TabsContent>


        </Tabs>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Pricing Rule</DialogTitle>
            <DialogDescription>
              Update the pricing for this item
            </DialogDescription>
          </DialogHeader>
          {editingRule && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Label</Label>
                <Input
                  value={editingRule.label}
                  onChange={(e) => setEditingRule({ ...editingRule, label: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingRule.description || ""}
                  onChange={(e) => setEditingRule({ ...editingRule, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Rate</Label>
                  <Input
                    type="number"
                    value={editingRule.default_rate}
                    onChange={(e) => setEditingRule({ ...editingRule, default_rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                {editingRule.pricing_method === "range" && (
                  <div className="space-y-2">
                    <Label>Max Rate</Label>
                    <Input
                      type="number"
                      value={editingRule.rate_max || ""}
                      onChange={(e) => setEditingRule({ ...editingRule, rate_max: parseFloat(e.target.value) || null })}
                    />
                  </div>
                )}
              </div>
              {editingRule.calculator_type === "inventory" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Units</Label>
                    <Input
                      type="number"
                      value={editingRule.tier_config?.min_units ?? 0}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        tier_config: { ...editingRule.tier_config, min_units: parseInt(e.target.value) || 0 }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Units</Label>
                    <Input
                      type="number"
                      value={editingRule.tier_config?.max_units >= 999999999 ? "" : (editingRule.tier_config?.max_units ?? "")}
                      placeholder="∞ (leave empty for no limit)"
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setEditingRule({
                          ...editingRule,
                          tier_config: { ...editingRule.tier_config, max_units: isNaN(val) ? 999999999 : val }
                        });
                      }}
                    />
                  </div>
                </div>
              )}
              {editingRule.calculator_type === "interior_modeling" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min SF</Label>
                    <Input
                      type="number"
                      value={editingRule.tier_config?.min_sqft ?? 0}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        tier_config: { ...editingRule.tier_config, min_sqft: parseInt(e.target.value) || 0 }
                      })}
                      data-testid="edit-interior-tier-min-sqft"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max SF</Label>
                    <Input
                      type="number"
                      value={editingRule.tier_config?.max_sqft >= 999999999 ? "" : (editingRule.tier_config?.max_sqft ?? "")}
                      placeholder="∞ (leave empty for no limit)"
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setEditingRule({
                          ...editingRule,
                          tier_config: { ...editingRule.tier_config, max_sqft: isNaN(val) ? 999999999 : val }
                        });
                      }}
                      data-testid="edit-interior-tier-max-sqft"
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={editingRule.active ? "active" : "inactive"}
                  onValueChange={(v) => setEditingRule({ ...editingRule, active: v === "active" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRule}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tier Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Tier</DialogTitle>
            <DialogDescription>
              Add a new {addingToCategory === "complexity" ? "complexity" : "site environment"} pricing tier
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tier Name</Label>
              <Input
                value={newTier.label}
                onChange={(e) => setNewTier({ ...newTier, label: e.target.value })}
                placeholder={addingToCategory === "complexity" ? "e.g., XXL (50+ Stories)" : "e.g., Premium"}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newTier.description}
                onChange={(e) => setNewTier({ ...newTier, description: e.target.value })}
                placeholder="Brief description of this tier"
              />
            </div>
            <div className="space-y-2">
              <Label>Rate ($)</Label>
              <Input
                type="number"
                value={newTier.default_rate}
                onChange={(e) => setNewTier({ ...newTier, default_rate: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setNewTier({ label: "", description: "", default_rate: 0 });
              setAddingToCategory(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddTier} disabled={!newTier.label}>
              Add Tier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
